import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChartLegend } from "./ChartLegend.js";
import { axisScale, round2 } from "./scale.js";
// ── Geometry, in viewBox units ────────────────────────────────────────────────
// The SVG scales to its container (viewBox + width:100%), so these are proportions
// as much as pixels: at a typical ~700px render one unit is a little over 1px.
const VIEW_W = 640;
const VIEW_H = 300;
// PAD_LEFT is measured, not guessed: the tick rail must hold a formatted money label
// at TICK_FONT_SIZE without the viewport cropping it. Geist Mono advances ~0.6em, so
// 60 units of rail (68 minus the 8-unit gutter) holds "£1,200,000". A narrower rail
// silently crops the first character of the largest tick — the one that sets the scale.
const PAD_LEFT = 68;
const PAD_RIGHT = 12;
const PAD_TOP = 12;
const PAD_BOTTOM = 34; // the x-label band — INSIDE the viewBox, so it can never be
// clipped by a container height (the "axis excluded from the box" anti-pattern)
const PLOT_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = VIEW_H - PAD_TOP - PAD_BOTTOM;
const BASELINE_Y = PAD_TOP + PLOT_H;
/** Bars are capped, never stretched to fill their band: the leftover band IS the air. */
const MAX_COLUMN_W = 24;
const COLUMN_BAND_FRACTION = 0.6;
/** The surface gap that separates touching segments. White does the separating — never a stroke. */
const SEGMENT_GAP = 2;
/** Rounded data-end on the top of the stack; the baseline end stays square. */
const CORNER_R = 4;
const TICK_INTERVALS = 4;
/** Ticks and category labels: small, recessive, and sized so the rail above holds them. */
const TICK_FONT_SIZE = 10;
/**
 * The top segment's outline: rounded at the data end, square at the baseline.
 * Radius shrinks with a short or narrow segment so the corner can never eat the mark.
 */
function topRoundedPath(x, y, w, h) {
    const r = round2(Math.max(0, Math.min(CORNER_R, w / 2, h)));
    const left = round2(x);
    const top = round2(y);
    const width = round2(w);
    const height = round2(h);
    if (r === 0)
        return `M${left} ${top}h${width}v${height}h${-width}Z`;
    return (`M${left} ${round2(top + r)}` +
        `a${r} ${r} 0 0 1 ${r} ${-r}` +
        `h${round2(width - 2 * r)}` +
        `a${r} ${r} 0 0 1 ${r} ${r}` +
        `v${round2(height - r)}` +
        `h${-width}Z`);
}
function assertPlottable(series, categories) {
    for (const s of series) {
        if (s.values.length !== categories.length) {
            throw new Error(`StackedBarChart: series "${s.key}" has ${s.values.length} values for ` +
                `${categories.length} categories — the arrays are index-aligned, so a mismatch ` +
                `means the projection dropped or duplicated a period.`);
        }
        for (let i = 0; i < s.values.length; i += 1) {
            const v = s.values[i];
            if (!Number.isFinite(v) || v < 0) {
                // Loud on purpose: a stack cannot honestly show a negative contribution, and
                // clamping it to 0 would hide a real data fault behind a plausible picture.
                throw new Error(`StackedBarChart: series "${s.key}" value ${String(v)} at category ` +
                    `"${categories[i]}" is not a finite, non-negative number — a stacked column ` +
                    `cannot represent it.`);
            }
        }
    }
}
export function StackedBarChart({ categories, series, formatValue, ariaLabel, className, }) {
    // Nothing to draw is not an empty chart — the page owns its own empty state
    // (EmptyState), which can say something useful about why there is no data.
    if (categories.length === 0 || series.length === 0)
        return null;
    assertPlottable(series, categories);
    const totals = categories.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
    const { max: yMax, ticks } = axisScale(Math.max(...totals), TICK_INTERVALS);
    const band = PLOT_W / categories.length;
    const columnW = Math.min(MAX_COLUMN_W, band * COLUMN_BAND_FRACTION);
    const yOf = (value) => BASELINE_Y - (value / yMax) * PLOT_H;
    const legendEntries = series.map((s) => ({
        label: s.label,
        colorVar: s.colorVar,
        dimmed: s.dimmed,
    }));
    const label = ariaLabel ??
        `Stacked bar chart: ${series.length} series across ${categories.length} ` +
            `${categories.length === 1 ? "period" : "periods"}, ${categories[0]} to ` +
            `${categories[categories.length - 1]}; largest column ${formatValue(Math.max(...totals))}.`;
    return (_jsxs("div", { className: ["chart", "chart--stacked-bar", className ?? ""].filter(Boolean).join(" "), children: [_jsxs("svg", { className: "chart-bar", role: "img", "aria-label": label, viewBox: `0 0 ${VIEW_W} ${VIEW_H}`, style: { display: "block", width: "100%", height: "auto" }, children: [_jsx("g", { className: "chart-bar__ticks", children: ticks.map((tick) => {
                            const y = round2(yOf(tick));
                            return (_jsxs("g", { children: [_jsx("line", { x1: PAD_LEFT, y1: y, x2: PAD_LEFT + PLOT_W, y2: y, vectorEffect: "non-scaling-stroke", style: { stroke: "var(--ard-border)" }, strokeWidth: 1 }), _jsx("text", { className: "chart-bar__tick-label", x: PAD_LEFT - 8, y: y, textAnchor: "end", dominantBaseline: "middle", fontSize: TICK_FONT_SIZE, style: {
                                            fill: "var(--ard-muted)",
                                            fontFamily: "var(--font-mono)",
                                            fontVariantNumeric: "tabular-nums",
                                        }, children: formatValue(tick) })] }, `tick-${tick}`));
                        }) }), categories.map((category, columnIndex) => {
                        const x = PAD_LEFT + band * columnIndex + (band - columnW) / 2;
                        // Index of the last series with a drawable value: that one, and only that
                        // one, gets the rounded data-end.
                        let topIndex = -1;
                        for (let s = 0; s < series.length; s += 1) {
                            if ((series[s].values[columnIndex] ?? 0) > 0)
                                topIndex = s;
                        }
                        let cumulative = 0;
                        return (_jsxs("g", { className: "chart-bar__column", children: [series.map((s, seriesIndex) => {
                                    const value = s.values[columnIndex] ?? 0;
                                    const yTopRaw = yOf(cumulative + value);
                                    const yBottom = yOf(cumulative);
                                    cumulative += value;
                                    // The surface gap comes out of THIS segment's top whenever something
                                    // is stacked above it — 2 units of bare surface, no stroke.
                                    const hasSegmentAbove = value > 0 && seriesIndex < topIndex;
                                    const yTop = hasSegmentAbove ? yTopRaw + SEGMENT_GAP : yTopRaw;
                                    const height = Math.max(0, yBottom - yTop);
                                    const title = (_jsx("title", { children: `${s.label} — ${category}: ${formatValue(value)}` }));
                                    const shared = {
                                        "data-series": s.key,
                                        "data-value": String(value),
                                    };
                                    if (value > 0 && seriesIndex === topIndex) {
                                        return (_jsx("path", { className: "chart-bar__segment chart-bar__segment--top", ...shared, d: topRoundedPath(x, yTop, columnW, height), style: { fill: s.colorVar }, children: title }, s.key));
                                    }
                                    // Zero-valued segments land here too, at height 0: present in the DOM,
                                    // present in the legend, drawing nothing.
                                    return (_jsx("rect", { className: `chart-bar__segment${value === 0 ? " chart-bar__segment--zero" : ""}`, ...shared, x: round2(x), y: round2(yTop), width: round2(columnW), height: round2(height), style: { fill: s.colorVar }, children: title }, s.key));
                                }), _jsx("text", { className: "chart-bar__category-label", x: round2(x + columnW / 2), y: BASELINE_Y + 18, textAnchor: "middle", fontSize: TICK_FONT_SIZE, style: { fill: "var(--ard-text-2)", fontFamily: "var(--font-ui)" }, children: category })] }, `col-${columnIndex}:${category}`));
                    }), _jsx("line", { className: "chart-bar__baseline", x1: PAD_LEFT, y1: BASELINE_Y, x2: PAD_LEFT + PLOT_W, y2: BASELINE_Y, vectorEffect: "non-scaling-stroke", style: { stroke: "var(--ard-border-strong)" }, strokeWidth: 1 })] }), _jsx(ChartLegend, { entries: legendEntries, className: "chart__legend" })] }));
}
export default StackedBarChart;
//# sourceMappingURL=StackedBarChart.js.map